<template>
  <div class="projects-grid-container">
    <div class="projects-grid">
      <div
        v-for="project in projects"
        :key="project.id"
        class="project-card modern-project-card"
        @click="handleProjectClick(project)"
      >
        <!-- Project Icon -->
        <div class="project-card-icon">
          <i :class="project.icon"></i>
        </div>

        <!-- Project Content -->
        <div class="project-card-content">
          <h3 class="project-card-title">{{ project.title }}</h3>
          <p v-if="project.subtitle" class="project-card-subtitle">{{ project.subtitle }}</p>
          <p class="project-card-description">{{ project.description }}</p>

          <!-- Project Features (if available) -->
          <div v-if="project.features && project.features.length" class="project-features">
            <h4 class="features-title"><i class="fas fa-star"></i> Key Features</h4>
            <ul class="features-list">
              <li v-for="(feature, index) in project.features.slice(0, 3)" :key="index">
                <i class="fas fa-check-circle"></i>
                <span>{{ feature }}</span>
              </li>
            </ul>
          </div>

          <!-- Project Meta -->
          <div class="project-card-meta">
            <div class="meta-item">
              <i class="fas fa-calendar"></i>
              <span>{{ project.duration }}</span>
            </div>
            <div class="meta-item">
              <i class="fas fa-signal"></i>
              <span>{{ project.difficulty }}</span>
            </div>
            <div v-if="project.estimated_hours" class="meta-item">
              <i class="fas fa-clock"></i>
              <span>{{ project.estimated_hours }}</span>
            </div>
          </div>

          <!-- Project Tools (if available) -->
          <div v-if="project.tools && project.tools.length" class="project-tools">
            <span v-for="tool in project.tools.slice(0, 3)" :key="tool" class="tool-badge">{{ tool }}</span>
          </div>

        </div>

        <!-- Project Status Badge -->
        <div v-if="project.status" class="project-status-badge" :class="`status-${project.status}`">
          {{ getStatusLabel(project.status) }}
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!projects || projects.length === 0" class="empty-state">
      <i class="fas fa-project-diagram"></i>
      <h3>No Projects Available</h3>
      <p>Projects will be added soon. Stay tuned!</p>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Project {
  id: string | number
  title: string
  subtitle?: string
  description: string
  icon: string
  duration: string
  difficulty: string
  estimated_hours?: string
  features?: string[]
  learning_outcomes?: string[]
  tools?: string[]
  deliverables?: string[]
  tags?: string[]
  status?: 'locked' | 'available' | 'in-progress' | 'completed'
}

interface Props {
  projects: Project[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'project-click', project: Project): void
}>()

const handleProjectClick = (project: Project) => {
  if (project.status !== 'locked') {
    emit('project-click', project)
  }
}

const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    'locked': 'Locked',
    'available': 'Available',
    'in-progress': 'In Progress',
    'completed': 'Completed'
  }
  return labels[status] || status
}
</script>

<style scoped>
.projects-grid-container {
  padding: 0;
  width: 100%;
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  width: 100%;
}

.modern-project-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: 20px;
  padding: 28px;
  border: 2px solid rgba(139, 92, 246, 0.12);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 18px;
  overflow: hidden;
}

.modern-project-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #2563eb, #1a1a1a);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.modern-project-card:hover::before {
  opacity: 1;
}

.modern-project-card:hover {
  transform: translateY(-6px);
  border-color: rgba(139, 92, 246, 0.4);
  box-shadow: 0 20px 40px rgba(139, 92, 246, 0.2);
}

.project-card-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.project-card-icon i {
  font-size: 28px;
  color: white;
}

.project-card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.project-card-title {
  font-size: 21px;
  font-weight: 700;
  color: #1E293B;
  margin: 0;
  line-height: 1.3;
  background: linear-gradient(135deg, #1E293B, #475569);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.project-card-subtitle {
  font-size: 13px;
  font-weight: 600;
  color: #3b82f6;
  margin: -8px 0 4px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.project-card-description {
  font-size: 14px;
  color: #64748B;
  line-height: 1.7;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Project Features */
.project-features {
  margin-top: 8px;
  padding: 16px;
  background: rgba(139, 92, 246, 0.04);
  border-radius: 12px;
  border-left: 3px solid #3b82f6;
}

.features-title {
  font-size: 13px;
  font-weight: 700;
  color: #1E293B;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.features-title i {
  color: #3b82f6;
  font-size: 12px;
}

.features-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.features-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
}

.features-list li i {
  color: #22C55E;
  font-size: 14px;
  margin-top: 2px;
  flex-shrink: 0;
}

/* Project Tools */
.project-tools {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.tool-badge {
  font-size: 11px;
  font-weight: 600;
  color: #3b82f6;
  background: rgba(139, 92, 246, 0.1);
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(139, 92, 246, 0.2);
  transition: all 0.2s ease;
}

.tool-badge:hover {
  background: rgba(139, 92, 246, 0.15);
  border-color: rgba(139, 92, 246, 0.3);
}

/* View Details Button */
.view-details-btn {
  margin-top: 12px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.view-details-btn:hover {
  transform: translateX(4px);
  box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
}

.view-details-btn i {
  transition: transform 0.3s ease;
}

.view-details-btn:hover i {
  transform: translateX(4px);
}

.project-card-meta {
  display: flex;
  gap: 16px;
  margin-top: 4px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748B;
  font-weight: 500;
}

.meta-item i {
  color: #3b82f6;
  font-size: 14px;
}

.project-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.tag {
  font-size: 11px;
  font-weight: 600;
  color: #3b82f6;
  background: rgba(139, 92, 246, 0.1);
  padding: 4px 10px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.project-status-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  font-size: 11px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-locked {
  background: rgba(148, 163, 184, 0.2);
  color: #64748B;
}

.status-available {
  background: rgba(34, 197, 94, 0.15);
  color: #16A34A;
}

.status-in-progress {
  background: rgba(249, 115, 22, 0.15);
  color: #EA580C;
}

.status-completed {
  background: rgba(59, 130, 246, 0.15);
  color: #2563EB;
}

.project-card.locked {
  opacity: 0.6;
  cursor: not-allowed;
}

.project-card.locked:hover {
  transform: none;
  box-shadow: none;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748B;
}

.empty-state i {
  font-size: 64px;
  color: #CBD5E1;
  margin-bottom: 20px;
}

.empty-state h3 {
  font-size: 24px;
  font-weight: 700;
  color: #475569;
  margin: 0 0 12px 0;
}

.empty-state p {
  font-size: 16px;
  color: #64748B;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .projects-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .project-card {
    padding: 20px;
  }

  .project-card-title {
    font-size: 18px;
  }

  .project-card-description {
    font-size: 13px;
  }
}
</style>
